import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { IEditLotModalState } from './interfaces';
import { deleteRequest, getRequest, postRequest, putRequest } from 'api/requests';
import { ILotPhoto } from 'interfaces/main';
import { defaultLot } from 'consts/default';

// Открытие окна с существующей организацией
export const openEditLotModal = createAction('editLotModal/openEditLotModal', (lotId: number) => ({
	payload: lotId,
}));

// Закрытие окна редактирования организации
export const closeModal = createAction('editLotModal/closeModal');

// Загрузка всей организации по ID
export const getLotById = createAsyncThunk(
	'editLotModal/getLotById',
	async ({ id }: { id: number }, { rejectWithValue }) => {
		try {
			const response = await getRequest(`lot/${id}`, {}, true);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Загрузка фотографий
export const uploadPhotos = createAsyncThunk(
	'editLotModal/uploadPhotos',
	async ({ files, lotId }: { files: File[]; lotId: number }, { rejectWithValue }) => {
		try {
			// 👇 Create new FormData object and append files
			const data = new FormData();

			files.forEach((file, i) => {
				data.append(`file-${i}`, file, file.name);
			});

			const response = await postRequest(`lotPhoto/upload?lotId=${lotId}`, data);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Загрузка обложки
export const uploadCover = createAsyncThunk(
	'editLotModal/uploadCover',
	async ({ file, lotId }: { file: File; lotId: number }, { rejectWithValue }) => {
		try {
			// 👇 Create new FormData object and append files
			const data = new FormData();
			data.append(`cover`, file, file.name);

			const response = await postRequest(`lotCover/upload?lotId=${lotId}`, data);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление фотографии
export const removePhoto = createAsyncThunk(
	'editLotModal/removePhoto',
	async ({ id }: { id: number }, { rejectWithValue }) => {
		try {
			const response = await deleteRequest(`lotPhoto/${id}`);

			return response.data.id;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление обложки
export const removeCover = createAsyncThunk(
	'editLotModal/removeCover',
	async (id: number, { rejectWithValue }) => {
		try {
			return await deleteRequest(`lotCover/${id}`);
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Установка значения в поле организации
export const changeLot = createAsyncThunk(
	'editLotModal/changeLot',
	async (
		{
			key,
			lotId,
			value,
		}: {
			key: string;
			lotId: number;
			value: string | number | boolean;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await putRequest(
				`lot/${lotId}`,
				{
					[key]: value,
				},
				true,
			);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

const initialState: IEditLotModalState = {
	data: defaultLot,
	isLoading: false,
	isPhotosLoading: false,
	isCoverLoading: false,
	isVisible: false,
};

const editLotModalSlice = createSlice({
	name: 'editLotModal',
	initialState,
	reducers: {
		closeModal: () => initialState,
		openEditLotModal: (_, { payload }) => ({
			...initialState,
			isVisible: true,
			data: {
				...defaultLot,
				id: payload,
			},
		}),
	},
	extraReducers: (builder) => {
		builder
			.addCase(getLotById.pending, (state) => ({
				...state,
				isLoading: true,
			}))
			.addCase(getLotById.rejected, (state) => ({
				...state,
				isLoading: false,
			}))
			.addCase(getLotById.fulfilled, (state, { payload }) => ({
				...state,
				isLoading: false,
				data: {
					...state.data,
					...payload,
				},
			}))
			.addCase(changeLot.fulfilled, (state, { payload }) => ({
				...state,
				data: {
					...state.data,
					...payload,
				},
			}))
			.addCase(uploadPhotos.pending, (state) => ({
				...state,
				isPhotosLoading: true,
			}))
			.addCase(uploadPhotos.rejected, (state) => ({
				...state,
				isPhotosLoading: false,
			}))
			.addCase(uploadPhotos.fulfilled, (state, { payload }) => ({
				...state,
				isPhotosLoading: false,
				data: {
					...state.data,
					lotPhotos: [...state.data?.lotPhotos, ...payload],
				},
			}))
			.addCase(uploadCover.pending, (state) => ({
				...state,
				isCoverLoading: true,
			}))
			.addCase(uploadCover.rejected, (state) => ({
				...state,
				isCoverLoading: false,
			}))
			.addCase(uploadCover.fulfilled, (state, { payload }) => ({
				...state,
				isCoverLoading: false,
				data: {
					...state.data,
					lotCover: payload,
				},
			}))
			.addCase(removePhoto.pending, (state) => ({
				...state,
				isPhotosLoading: true,
			}))
			.addCase(removePhoto.rejected, (state) => ({
				...state,
				isPhotosLoading: false,
			}))
			.addCase(removePhoto.fulfilled, (state, { payload }) => ({
				...state,
				isPhotosLoading: false,
				data: {
					...state.data,
					lotPhotos: state.data.lotPhotos.filter((item: ILotPhoto) => item.id !== payload),
				},
			}))
			.addCase(removeCover.pending, (state) => ({
				...state,
				isCoverLoading: true,
			}))
			.addCase(removeCover.rejected, (state) => ({
				...state,
				isCoverLoading: false,
			}))
			.addCase(removeCover.fulfilled, (state, { payload }) => ({
				...state,
				isCoverLoading: false,
				data: {
					...state.data,
					lotCover: null,
				},
			}));
	},
});

// Селекторы
export const EditLotModalSelector = (state: RootState): IEditLotModalState => state.editLotModal;

export const EditLotModalLoadingSelector = createSelector(
	EditLotModalSelector,
	({ isLoading }) => isLoading,
);

export const EditLotModalPhotosLoadingSelector = createSelector(
	EditLotModalSelector,
	({ isPhotosLoading }) => isPhotosLoading,
);

export const EditLotModalCoverLoadingSelector = createSelector(
	EditLotModalSelector,
	({ isCoverLoading }) => isCoverLoading,
);

export const EditLotModalVisibleSelector = createSelector(
	EditLotModalSelector,
	({ isVisible }) => isVisible,
);

export const EditLotModalDataSelector = createSelector(EditLotModalSelector, ({ data }) => data);

export default editLotModalSlice.reducer;
